import { DETECTION_EVENTS_BATCH_MAX } from '@groupe-reca/contracts'
import { ServerCommandError } from '@/infrastructure/supabase/client'
import type { JournalEntry, LocalStore } from '@/platform/localStore'
import type { MissionRepository } from '../MissionRepository'

export interface JournalSyncResult {
  sent: number
  rejected: number
  /** Terminaisons AUTO acceptées par le serveur (la liste « À faire » doit être relue). */
  completed: number
  pending: number
  /** `NETWORK` (on réessaiera) ou code de refus du serveur. */
  error: string | null
}

/**
 * Envoi idempotent du journal de détection. Un événement n'est retiré qu'après accusé du serveur (reçu, déjà reçu ou
 * refusé pour une raison définitive). La terminaison AUTO d'une résidence part après l'accusé de son événement, avec
 * l'identifiant de l'événement comme clé d'idempotence : un renvoi après coupure ne crée jamais de second travail.
 * Un refus du serveur (hors mode AUTO, résidence en problème, mission terminée) est définitif : l'entrée est retirée.
 */
export class JournalSync {
  private running: Promise<JournalSyncResult> | null = null

  constructor(
    private readonly store: LocalStore,
    private readonly repository: MissionRepository,
  ) {}

  flush(): Promise<JournalSyncResult> {
    this.running ??= this.run().finally(() => {
      this.running = null
    })
    return this.running
  }

  private async run(): Promise<JournalSyncResult> {
    let sent = 0
    let rejected = 0
    let completed = 0
    let error: JournalSyncResult['error'] = null
    try {
      for (;;) {
        const batch = await this.store.pendingJournal(DETECTION_EVENTS_BATCH_MAX)
        const missionId = batch[0]?.missionId
        if (missionId === undefined) break
        const entries = batch.filter((entry) => entry.missionId === missionId)
        const ack = await this.repository.sendDetectionEvents(
          missionId,
          entries.map((entry) => ({
            id: entry.id,
            itemId: entry.itemId,
            kind: entry.kind,
            occurredAt: entry.occurredAt,
            engineVersion: entry.engineVersion,
            payload: entry.payload,
          })),
        )
        const rejectedIds = new Set(ack.rejected.map((item) => item.id))
        const received = new Set(ack.acknowledged)
        rejected += rejectedIds.size
        sent += received.size
        const done: string[] = [...rejectedIds]
        for (const entry of entries) {
          if (!received.has(entry.id)) continue
          if (entry.completeItemId !== null) {
            const outcome = await this.complete(entry, entry.completeItemId)
            if (outcome === 'ok') completed += 1
          }
          done.push(entry.id)
        }
        if (done.length === 0) break
        await this.store.removeJournal(done)
        if (entries.length < DETECTION_EVENTS_BATCH_MAX && batch.length === entries.length) break
      }
    } catch (failure) {
      error = failure instanceof ServerCommandError ? failure.code : 'NETWORK'
    }
    return { sent, rejected, completed, pending: await this.store.countJournal(), error }
  }

  /** `ok`, ou `refused` (définitif). Une erreur réseau remonte : l'entrée reste et la même clé sera rejouée. */
  private async complete(entry: JournalEntry, itemId: string): Promise<'ok' | 'refused'> {
    try {
      await this.repository.complete({
        idempotencyKey: entry.id,
        itemId,
        source: 'AUTO',
        detectionEventId: entry.id,
      })
      return 'ok'
    } catch (failure) {
      if (failure instanceof ServerCommandError) return 'refused'
      throw failure
    }
  }
}
